<script setup>
    import MainHeader from '@/components/HeaderComponent.vue';
    import NewsDetail from '@/components/modal/NewsDetailComponent.vue';
    import { ref, inject, getCurrentInstance } from 'vue';
    import { useHomeStore } from '@/stores/home/homeStore';
    import { onMounted } from 'vue';

    // Utis
    import utils from '@/commonProject/helpers/utils';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // APIのドメイン部を管理する。
    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // メニュー取得
    const homeStore = useHomeStore();

    const toggle = ref(false);

    const menuNewArrival = ref({
        DscCng: false, //開示カウンセリング
        HarRep: false, //ハラスメント報告
        CrsRep: false, //危機介入報告
        MonRep: false, //月次レポート
    });

    // お知らせ詳細モーダル
    const newsDetailDispFlg = ref('');
    const newsDetailComponent = ref();
    async function newsDetailOpen(noticeBulletinPk, noticeReaded, index) {
        newsDetailComponent.value.newsDetailSet(API_URL, noticeBulletinPk, noticeReaded, index);
        newsDetailDispFlg.value = 'block';
    }

    async function newsDetailClose() {
        newsDetailDispFlg.value = '';

        // お知らせモーダルが閉じられたので、改めてお知らせの一覧を取得しなおす。
        await getNewsList();
    }

    async function closeNewsDetailAndOpenContactModal() {
        newsDetailDispFlg.value = '';

        // お知らせモーダルが閉じられたので、改めてお知らせの一覧を取得しなおす。
        await getNewsList();

        document.getElementById('contactModal').click();
    }

    toggle.value = true;

    /**
     * メニュー取得処理
     */
    async function getMenu() {
        const queryParam = {};
        const response = await homeStore.getMenuAction(API_URL, queryParam);
        if (response.success) {
            if (!utils.isEmptyArray(homeStore.Menu1)) {
                for (const menu of homeStore.Menu1) {
                    if (!utils.isNullEmpty(menu.NewArrival)) {
                        await getNewArrival(menu.NewArrival);
                    }
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * メニューの新着情報を取得する
     */
    async function getNewArrival(FuncNm) {
        const queryParam = {
            FuncNm: FuncNm,
        };
        // 新着情報確認APIをコールする。
        const response = await homeStore.getNewArrivalAction(API_URL, queryParam);

        if (response.success) {
            menuNewArrival.value[FuncNm] = response.responseBody.Result;
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * お知らせ一覧取得処理
     */
    async function getNewsList() {
        // Storeに格納されている、取得したお知らせ一覧のデータを初期化する。
        homeStore.List = [];

        const response = await homeStore.getNewsListAction(API_URL, null);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    onMounted(async () => {
        // 初期化
        homeStore.resetHomeStore();

        // メニューを取得
        await getMenu();

        // お知らせ一覧取得
        await getNewsList();
    });
</script>

<style></style>

<template>
    <div v-show="toggle">
        <section id="main">
            <section id="overlay" :style="{ display: newsDetailDispFlg }">
                <NewsDetail ref="newsDetailComponent" @newsDetailModal:close="newsDetailClose" @newsDetailModal:openContactModalFromNewDetailModal="closeNewsDetailAndOpenContactModal" />
            </section>
            <MainHeader />
            <div id="home">
                <div id="leftBlock">
                    <ul>
                        <template v-for="menu in homeStore.Menu1" :key="menu.index">
                            <li :class="{ emphasis: !utils.isNullEmpty(menu.NewArrival) && menuNewArrival[menu.NewArrival] === true }">
                                <template v-if="menu.Type === 'Button'">
                                    <template v-if="menu.Destination !== null && (menu.Destination.indexOf('https://') !== -1 || menu.Destination.indexOf('http://') !== -1)">
                                        <a :href="menu.Destination" v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" />
                                        <!--外部へのリンクが設定されている場合-->
                                    </template>
                                    <template v-else-if="menu.Destination === '/monthlyReport'">
                                        <router-link :to="{ path: menu.Destination }" v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" target="_blank" />
                                        <!--月次レポートは別タブで表示する-->
                                    </template>
                                    <template v-else>
                                        <router-link :to="{ path: menu.Destination }" v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" />
                                        <!--上記以外のメニュー-->
                                    </template>
                                </template>
                                <template v-else-if="menu.Type === 'Guidance1' || menu.Type === 'Guidance2' || menu.Type === 'Guidance3' || menu.Type === 'Guidance4' || menu.Type === 'Guidance5'">
                                    <p v-html="menu.Caption" :style="{ 'background-color': menu.Bgcolor }" />
                                    <!--固定プレート-->
                                </template>
                            </li>
                        </template>
                    </ul>
                </div>
                <div id="rightBlock">
                    <div class="menu">
                        <ul>
                            <template v-for="menu in homeStore.Menu2" :key="menu.index">
                                <template v-if="menu.Destination !== null && menu.Destination.indexOf('/file/download') !== -1">
                                    <!--ファイルダウンロード-->
                                    <router-link :to="{ path: menu.Destination }" class="btnText">{{ menu.Caption }}</router-link>
                                </template>
                                <template v-else>
                                    <!--外部へのリンクが設定されている場合-->
                                    <a :href="menu.Destination" v-html="menu.Caption" class="btnText" target="_blank" />
                                </template>
                            </template>
                        </ul>
                    </div>
                    <div class="news">
                        <h2 class="newsTitle">{{ $t('home.parts.blockTitle1') }}</h2>
                        <ul class="newsArea">
                            <template v-for="(news, index) in homeStore.List" :key="news.index">
                                <li :class="{ unread: !news.NoticeReaded }">
                                    <button type="button" @click="newsDetailOpen(news.NoticeBulletinPk, news.NoticeReaded, index)">
                                        <span class="date">{{ news.NoticeDt }}</span>
                                        <span class="title">{{ news.NoticeSubject }}</span>
                                    </button>
                                </li>
                            </template>
                        </ul>
                    </div>
                </div>
                <!--#rightBlock-->
            </div>
        </section>
    </div>
    <p v-show="!toggle">ロード中です</p>
</template>
